import { Box, CircularProgress, Typography } from '@mui/material'
import {
  adjustItemBinQuantities,
  getBinsInLocation,
  getNetSuiteItemInventory,
  getNetSuiteLocations,
  setPreferredBin
} from 'api/netsuite'
import { SnackbarSeverity } from 'constant'
import { LoadingContext } from 'contexts/LoadingContextProvider'
import { SnackContext } from 'contexts/SnackContextProvider'
import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import LocationSelector, {
  type SelectedLocation
} from 'components/ItemLookup/LocationSelector'
import ItemCodeInput from 'components/ItemLookup/ItemCodeInput'
import BinListDisplay from 'components/ItemLookup/BinListDisplay'
import TransferBinForm from 'components/ItemLookup/TransferBinForm'

const ItemLookup = () => {
  const navigate = useNavigate()
  const { company } = useParams()
  const [locations, setLocations] = useState<any>(null)
  const [selectedLocation, setSelectedLocation] =
    useState<SelectedLocation | null>(null)
  const [itemCode, setItemCode] = useState('')
  const [itemName, setItemName] = useState('')
  const [itemUpcCode, setItemUpcCode] = useState('')
  const [itemBins, setItemBins] = useState<any>(null)
  const { handleOpenSnackbar } = useContext(SnackContext)
  const { setLoading, loading } = useContext(LoadingContext)
  const [fromBin, setFromBin] = useState('')
  const [locationBins, setLocationBins] = useState<
    { id: string; binnumber: string; location: string }[]
  >([])

  const getItemBinsInfo = async () => {
    if (!itemCode.trim()) {
      handleOpenSnackbar(SnackbarSeverity.Warning, 'Please enter an item code')
      return
    }
    setLoading(true)
    try {
      const itemResult = await getNetSuiteItemInventory(
        company!,
        itemCode,
        String(selectedLocation!.id)
      )
      if (itemResult.data.success) {
        setItemBins(itemResult.data.item.filteredItemBins)
        setItemName(itemResult.data.item.itemName)
        setItemUpcCode(itemResult.data.item.itemUpcCode)
      } else {
        handleOpenSnackbar(SnackbarSeverity.Warning, 'No data found')
      }
    } catch (error) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        'Error fetching item inventory'
      )
    } finally {
      setLoading(false)
    }
  }

  const displayedBinsRows =
    itemBins?.map((bin: any) => ({
      id: bin.binNumber.id,
      bin: bin.binNumber.refName,
      company,
      preferred: bin.preferredBin ? 'Yes' : 'No',
      onHand: bin.onHand || 0,
      available: bin.onHandAvail || 0
    })) ?? []

  const getLocations = async () => {
    setLoading(true)
    try {
      const locationsResult = await getNetSuiteLocations(company!)
      if (locationsResult.data.success) {
        setLocations(locationsResult.data.locations)
      } else {
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          locationsResult.data.errorMessage || 'Error fetching locations'
        )
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.errorMessage ||
        error.response?.data?.message ||
        error.message ||
        'Error fetching locations'
      console.error('Error fetching locations:', error)
      handleOpenSnackbar(SnackbarSeverity.Error, errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleSetPreferredBin = async () => {
    setLoading(true)
    try {
      const result = await setPreferredBin(
        company!,
        String(selectedLocation!.id),
        itemCode,
        fromBin
      )
      if (result.data.success) {
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          `${fromBin} set as preferred bin`
        )
        setFromBin('')
        setLocationBins([])
        await getItemBinsInfo()
      } else {
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          result.data.message || 'Failed to set preferred bin'
        )
      }
    } catch (error: any) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        error.response?.data?.message || 'Failed to set preferred bin'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleTransferSubmit = async (
    quantity: number,
    toBin: string,
    memo?: string
  ) => {
    setLoading(true)
    try {
      const result = await adjustItemBinQuantities(
        company!,
        String(selectedLocation!.id),
        itemCode,
        quantity,
        fromBin,
        toBin,
        memo
      )
      if (result.data.success) {
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          `Successfully transferred ${quantity} from ${fromBin} to ${toBin}`
        )
        setFromBin('')
        setLocationBins([])
        await getItemBinsInfo()
      } else {
        handleOpenSnackbar(
          SnackbarSeverity.Error,
          result.data.message || 'Error transferring bin quantity'
        )
      }
    } catch (error: any) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        error.response?.data?.message || 'Error transferring bin quantity'
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getLocations()
  }, [company])

  useEffect(() => {
    if (!fromBin || !selectedLocation || !company) return
    const fetchBins = async () => {
      setLoading(true)
      try {
        const res = await getBinsInLocation(
          company,
          String(selectedLocation.id)
        )
        if (res.data.success) {
          setLocationBins(res.data.bins ?? [])
        } else {
          setLocationBins([])
        }
      } catch {
        setLocationBins([])
      } finally {
        setLoading(false)
      }
    }
    fetchBins()
  }, [fromBin, selectedLocation?.id, company])

  return (
    <Box>
      {!selectedLocation ? (
        locations ? (
          <LocationSelector
            locations={locations}
            onSelectLocation={setSelectedLocation}
            onBack={() => navigate(-1)}
            loading={loading}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
              gap: 2
            }}
          >
            {loading && <CircularProgress />}
            <Typography color='text.secondary'>
              {loading ? 'Loading locations...' : 'No locations available'}
            </Typography>
          </Box>
        )
      ) : selectedLocation && !itemBins ? (
        <ItemCodeInput
          itemCode={itemCode}
          onItemCodeChange={setItemCode}
          onSubmit={getItemBinsInfo}
          onBack={() => {
            setSelectedLocation(null)
            setItemBins(null)
          }}
          loading={loading}
        />
      ) : selectedLocation && !fromBin ? (
        <BinListDisplay
          bins={displayedBinsRows}
          selectedLocation={selectedLocation.name}
          itemCode={itemCode}
          itemName={itemName}
          itemUpcCode={itemUpcCode}
          onBack={() => {
            setItemBins(null)
            setItemCode('')
          }}
          onEditBin={setFromBin}
          loading={loading}
        />
      ) : (
        <TransferBinForm
          fromBin={fromBin}
          locationBins={locationBins}
          onBack={() => {
            setFromBin('')
            setLocationBins([])
          }}
          onSubmit={handleTransferSubmit}
          onSetPreferred={handleSetPreferredBin}
          loading={loading}
        />
      )}
    </Box>
  )
}

export default ItemLookup
